import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Search,
  LogOut,
  BadgeCheck,
  ChevronRight,
  Bell,
  User,
  Timer,
  Activity,
  Calendar,
  MapPin,
  Mic,
  MicOff,
  Save,
  Target,
  TrendingUp,
  Info,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff
} from "lucide-react";
import { getGradeLabel, SubFactorInput, ToolSection, HitToolSection } from "../components/EvaluationTools";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NSB Logo Component
import { NSBLogo } from "../components/NSBLogo";

// Inline Evaluation History Component - Simplified
const EvalHistoryInline = ({ athleteId, athleteName }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
      <h3 className="text-xl font-bold text-gray-900 mb-2">Evaluation History</h3>
      <p className="text-gray-600 mb-4">{athleteName}</p>
      <p className="text-gray-500">View historical evaluations and compare scout grades.</p>
      <a 
        href={`/athlete/${athleteId}`} 
        className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
      >
        View Full Profile
      </a>
    </div>
  );
};

export default function ScoutDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("evaluate");
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Tool factor definitions (loaded from API)
  const [toolFactors, setToolFactors] = useState(null);
  
  // Expanded tool sections
  const [expandedTools, setExpandedTools] = useState({
    run: false,
    field: false,
    arm: false,
    pitcher_velocity: false,
    pitcher_command: false,
    pitcher_stuff: false,
    pitcher_pitchability: false,
    catcher_receive: false,
    catcher_throw: false,
    catcher_block: false,
    catcher_game_mgmt: false
  });

  // Mock players (will be replaced with API data)
  const mockPlayers = [
    { athlete_id: "p1", name: "Marcus Johnson", position_primary: "SS", age_decimal: 16.5, birthdate: "2009-07-15", graduation_year: 2027, school: "Central High", location: "Austin, TX", competition_level: "HS", bats: "R", throws: "R", height: "5'11", weight: 165 },
    { athlete_id: "p2", name: "Tyler Williams", position_primary: "P", age_decimal: 15.8, birthdate: "2010-03-22", graduation_year: 2028, school: "Westlake HS", location: "Austin, TX", competition_level: "HS", bats: "R", throws: "R", height: "6'1", weight: 175 },
    { athlete_id: "p3", name: "Jake Thompson", position_primary: "C", age_decimal: 17.2, birthdate: "2008-10-05", graduation_year: 2026, school: "Lake Travis", location: "Austin, TX", competition_level: "HS", bats: "L", throws: "R", height: "6'0", weight: 185 },
    { athlete_id: "p4", name: "Ryan Martinez", position_primary: "CF", age_decimal: 14.9, birthdate: "2011-02-12", graduation_year: 2029, school: "Anderson MS", location: "Austin, TX", competition_level: "MS", bats: "S", throws: "R", height: "5'8", weight: 145 },
  ];

  // Component-based evaluation state
  const [componentEval, setComponentEval] = useState({
    // Hit Tool (split into Avg + Power)
    hit_for_average: {},
    hit_for_power: {},
    hit_for_avg_override: null,
    hit_for_power_override: null,
    hit_overall_override: null,
    // Run Tool
    run_tool: {},
    run_override: null,
    // Field Tool
    field_tool: {},
    field_override: null,
    // Arm Tool
    arm_tool: {},
    arm_override: null,
    // Pitcher Tools
    pitcher_velocity: {},
    pitcher_velocity_override: null,
    pitcher_command: {},
    pitcher_command_override: null,
    pitcher_stuff: {},
    pitcher_stuff_override: null,
    pitcher_pitchability: {},
    pitcher_pitchability_override: null,
    // Catcher Tools
    catcher_receive: {},
    catcher_receive_override: null,
    catcher_throw: {},
    catcher_throw_override: null,
    catcher_block: {},
    catcher_block_override: null,
    catcher_game_mgmt: {},
    catcher_game_mgmt_override: null,
    // Raw Metrics
    sixty_time: "",
    exit_velocity: "",
    bat_speed: "",
    fastball_velocity: "",
    infield_velocity: "",
    outfield_velocity: "",
    catcher_velocity: "",
    pop_time: "",
    // Notes
    written_notes: "",
    voice_transcript: "",
    evaluation_context: "",
  });

  useEffect(() => {
    fetchData();
    fetchToolFactors();
  }, []);

  const fetchToolFactors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/evaluation/tool-factors`);
      if (res.ok) {
        const data = await res.json();
        setToolFactors(data);
      }
    } catch (err) {
      console.error("Failed to load tool factors:", err);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const userRes = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers,
      });

      if (!userRes.ok) {
        navigate("/login");
        return;
      }

      const userData = await userRes.json();
      setUser(userData);

      // Load players
      const playersRes = await fetch(`${API_URL}/api/players`, {
        credentials: "include",
        headers,
      });

      if (playersRes.ok) {
        const playersData = await playersRes.json();
        setPlayers(playersData.length > 0 ? playersData : mockPlayers);
      } else {
        setPlayers(mockPlayers);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setPlayers(mockPlayers);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    // Reset evaluation state
    setComponentEval({
      hit_for_average: {},
      hit_for_power: {},
      hit_for_avg_override: null,
      hit_for_power_override: null,
      hit_overall_override: null,
      run_tool: {},
      run_override: null,
      field_tool: {},
      field_override: null,
      arm_tool: {},
      arm_override: null,
      pitcher_velocity: {},
      pitcher_velocity_override: null,
      pitcher_command: {},
      pitcher_command_override: null,
      pitcher_stuff: {},
      pitcher_stuff_override: null,
      pitcher_pitchability: {},
      pitcher_pitchability_override: null,
      catcher_receive: {},
      catcher_receive_override: null,
      catcher_throw: {},
      catcher_throw_override: null,
      catcher_block: {},
      catcher_block_override: null,
      catcher_game_mgmt: {},
      catcher_game_mgmt_override: null,
      sixty_time: "",
      exit_velocity: "",
      bat_speed: "",
      fastball_velocity: "",
      infield_velocity: "",
      outfield_velocity: "",
      catcher_velocity: "",
      pop_time: "",
      written_notes: "",
      voice_transcript: "",
      evaluation_context: "",
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        transcribeAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setRecording(true);
      toast.info("Recording started...");
    } catch (err) {
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setRecording(false);
      toast.info("Processing audio...");
    }
  };

  const transcribeAudio = async (blob) => {
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.wav');
      
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/transcribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setComponentEval(prev => ({
          ...prev,
          voice_transcript: data.transcript
        }));
        toast.success("Transcription complete!");
      } else {
        toast.error("Transcription failed");
      }
    } catch (err) {
      toast.error("Transcription error");
    }
  };

  const handleSubmitEvaluation = async () => {
    if (!selectedPlayer) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      
      // Transform component eval to API format
      const payload = {
        athlete_id: selectedPlayer.athlete_id,
        evaluation_type: selectedPlayer.position_primary === "P" ? "pitcher" : 
                        selectedPlayer.position_primary === "C" ? "catcher" : "position_player",
        // Hit Tool (only for non-pitchers)
        hit_for_average: Object.keys(componentEval.hit_for_average).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.hit_for_average).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.hit_for_avg_override
        } : null,
        hit_for_power: Object.keys(componentEval.hit_for_power).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.hit_for_power).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.hit_for_power_override
        } : null,
        hit_tool_override: componentEval.hit_overall_override,
        // Run Tool
        run_tool: Object.keys(componentEval.run_tool).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.run_tool).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.run_override
        } : null,
        // Field Tool
        field_tool: Object.keys(componentEval.field_tool).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.field_tool).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.field_override
        } : null,
        // Arm Tool
        arm_tool: Object.keys(componentEval.arm_tool).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.arm_tool).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.arm_override
        } : null,
        // Pitcher Tools
        pitcher_velocity: selectedPlayer.position_primary === "P" && Object.keys(componentEval.pitcher_velocity).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.pitcher_velocity).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.pitcher_velocity_override
        } : null,
        pitcher_command: selectedPlayer.position_primary === "P" && Object.keys(componentEval.pitcher_command).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.pitcher_command).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.pitcher_command_override
        } : null,
        pitcher_stuff: selectedPlayer.position_primary === "P" && Object.keys(componentEval.pitcher_stuff).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.pitcher_stuff).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.pitcher_stuff_override
        } : null,
        pitcher_pitchability: selectedPlayer.position_primary === "P" && Object.keys(componentEval.pitcher_pitchability).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.pitcher_pitchability).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.pitcher_pitchability_override
        } : null,
        // Catcher Tools
        catcher_receive: selectedPlayer.position_primary === "C" && Object.keys(componentEval.catcher_receive).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.catcher_receive).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.catcher_receive_override
        } : null,
        catcher_throw: selectedPlayer.position_primary === "C" && Object.keys(componentEval.catcher_throw).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.catcher_throw).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.catcher_throw_override
        } : null,
        catcher_block: selectedPlayer.position_primary === "C" && Object.keys(componentEval.catcher_block).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.catcher_block).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.catcher_block_override
        } : null,
        catcher_game_mgmt: selectedPlayer.position_primary === "C" && Object.keys(componentEval.catcher_game_mgmt).length > 0 ? {
          ...Object.fromEntries(
            Object.entries(componentEval.catcher_game_mgmt).filter(([k, v]) => v?.grade)
          ),
          override_grade: componentEval.catcher_game_mgmt_override
        } : null,
        // Raw Metrics
        sixty_time: componentEval.sixty_time ? parseFloat(componentEval.sixty_time) : null,
        exit_velocity: componentEval.exit_velocity ? parseFloat(componentEval.exit_velocity) : null,
        bat_speed: componentEval.bat_speed ? parseFloat(componentEval.bat_speed) : null,
        fastball_velocity: componentEval.fastball_velocity ? parseFloat(componentEval.fastball_velocity) : null,
        infield_velocity: componentEval.infield_velocity ? parseFloat(componentEval.infield_velocity) : null,
        outfield_velocity: componentEval.outfield_velocity ? parseFloat(componentEval.outfield_velocity) : null,
        catcher_velocity: componentEval.catcher_velocity ? parseFloat(componentEval.catcher_velocity) : null,
        pop_time: componentEval.pop_time ? parseFloat(componentEval.pop_time) : null,
        // Notes
        written_notes: componentEval.written_notes,
        voice_transcript: componentEval.voice_transcript,
        evaluation_context: componentEval.evaluation_context,
        is_projection: false
      };
      
      const res = await fetch(`${API_URL}/api/scout/component-evaluation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Evaluation saved successfully!");
        console.log("Evaluation result:", data);
      } else {
        const errData = await res.json();
        toast.error(errData.detail || "Failed to save evaluation");
      }
    } catch (err) {
      toast.error("Error saving evaluation");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate tool grades for display
  const calcToolGrade = (values, factors, override) => {
    if (override) return { grade: override, confidence: 100 };
    if (!factors) return { grade: null, confidence: 0 };
    const evaluated = Object.entries(factors).filter(([k]) => values[k]?.grade);
    if (evaluated.length === 0) return { grade: null, confidence: 0 };
    const totalWeight = evaluated.reduce((sum, [k, f]) => sum + f.weight, 0);
    const allWeight = Object.values(factors).reduce((sum, f) => sum + f.weight, 0);
    const weightedSum = evaluated.reduce((sum, [k, f]) => sum + (values[k].grade * f.weight), 0);
    return { 
      grade: Math.round(weightedSum / totalWeight),
      confidence: Math.round((totalWeight / allWeight) * 100)
    };
  };

  // Get hit tool weights based on position
  const getHitToolWeights = (position) => {
    const weights = {
      "SS": { average: 0.70, power: 0.30 },
      "2B": { average: 0.65, power: 0.35 },
      "CF": { average: 0.65, power: 0.35 },
      "C": { average: 0.55, power: 0.45 },
      "3B": { average: 0.50, power: 0.50 },
      "RF": { average: 0.45, power: 0.55 },
      "LF": { average: 0.45, power: 0.55 },
      "1B": { average: 0.40, power: 0.60 },
    };
    return weights[position] || { average: 0.55, power: 0.45 };
  };

  const filteredPlayers = players.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.position_primary?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  const isPitcher = selectedPlayer?.position_primary === "P";
  const isCatcher = selectedPlayer?.position_primary === "C";
  const hitWeights = selectedPlayer ? getHitToolWeights(selectedPlayer.position_primary) : { average: 0.55, power: 0.45 };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="scout-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <NSBLogo className="h-10" light={false} />
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">Scout Evaluation Portal</h1>
                <p className="text-xs text-gray-500">Component-Based 20-80 Scale Evaluation</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">{user?.name || user?.email}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border">
            <TabsTrigger value="evaluate" data-testid="evaluate-tab">Create Evaluation</TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">My Evaluations</TabsTrigger>
          </TabsList>

          <TabsContent value="evaluate">
            <div className="grid grid-cols-12 gap-6">
              {/* Player Selection Panel */}
              <div className="col-span-4">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-semibold text-gray-900 mb-3">Select Player</h3>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search players..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="player-search-input"
                      />
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                    {filteredPlayers.map((player) => (
                      <button
                        key={player.athlete_id}
                        onClick={() => handlePlayerSelect(player)}
                        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                          selectedPlayer?.athlete_id === player.athlete_id ? "bg-blue-50 border-l-4 border-blue-500" : ""
                        }`}
                        data-testid={`player-${player.athlete_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white font-bold">
                            {player.name?.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 truncate">{player.name}</span>
                              <BadgeCheck className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            </div>
                            <div className="text-xs text-gray-500">
                              {player.position_primary} • {player.school} • {player.graduation_year}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Evaluation Form */}
              <div className="col-span-8">
                {selectedPlayer ? (
                  <div className="space-y-6">
                    {/* Player Header */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white text-2xl font-bold">
                            {selectedPlayer.name?.charAt(0)}
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900">{selectedPlayer.name}</h2>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                              <span className="font-medium text-blue-600">{selectedPlayer.position_primary}</span>
                              <span>•</span>
                              <span>{selectedPlayer.school}</span>
                              <span>•</span>
                              <span><MapPin className="inline h-3 w-3" /> {selectedPlayer.location}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>Age: {selectedPlayer.age_decimal?.toFixed(1)}</span>
                              <span>Class of {selectedPlayer.graduation_year}</span>
                              <span>{selectedPlayer.competition_level}</span>
                              <span>{selectedPlayer.height} / {selectedPlayer.weight}lbs</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* MLB 20-80 Scale Reference */}
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-4 text-white">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        MLB 20-80 Scale Reference (Age-Adjusted)
                      </h4>
                      <div className="flex gap-1 text-xs">
                        {[
                          { grade: "20-30", label: "Poor", color: "bg-red-500" },
                          { grade: "40", label: "Fringe", color: "bg-orange-500" },
                          { grade: "45", label: "Below Avg", color: "bg-yellow-500" },
                          { grade: "50", label: "Average", color: "bg-gray-400" },
                          { grade: "55", label: "Above Avg", color: "bg-blue-400" },
                          { grade: "60", label: "Plus", color: "bg-green-400" },
                          { grade: "70", label: "Plus-Plus", color: "bg-green-500" },
                          { grade: "80", label: "Elite", color: "bg-blue-800" },
                        ].map((item) => (
                          <div key={item.grade} className={`${item.color} px-3 py-2 rounded flex-1 text-center`}>
                            <div className="font-bold">{item.grade}</div>
                            <div className="text-[10px] opacity-90">{item.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tool Evaluation Sections */}
                    {!isPitcher && toolFactors && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Position Player Tools</h3>
                        
                        {/* Hit Tool (Composite) */}
                        <HitToolSection
                          hitForAvgFactors={toolFactors.position_player.hit_for_average}
                          hitForPowerFactors={toolFactors.position_player.hit_for_power}
                          hitForAvgValues={componentEval.hit_for_average}
                          hitForPowerValues={componentEval.hit_for_power}
                          onHitForAvgChange={(v) => setComponentEval(prev => ({ ...prev, hit_for_average: v }))}
                          onHitForPowerChange={(v) => setComponentEval(prev => ({ ...prev, hit_for_power: v }))}
                          hitForAvgOverride={componentEval.hit_for_avg_override}
                          hitForPowerOverride={componentEval.hit_for_power_override}
                          hitOverallOverride={componentEval.hit_overall_override}
                          onHitForAvgOverrideChange={(v) => setComponentEval(prev => ({ ...prev, hit_for_avg_override: v }))}
                          onHitForPowerOverrideChange={(v) => setComponentEval(prev => ({ ...prev, hit_for_power_override: v }))}
                          onHitOverallOverrideChange={(v) => setComponentEval(prev => ({ ...prev, hit_overall_override: v }))}
                          position={selectedPlayer.position_primary}
                          weights={hitWeights}
                        />

                        {/* Run Tool */}
                        <ToolSection
                          title="Run Tool"
                          description="Speed (65%) + Baserunning Instincts (35%)"
                          factors={toolFactors.position_player.run}
                          values={componentEval.run_tool}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, run_tool: v }))}
                          overrideGrade={componentEval.run_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, run_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.run_tool, toolFactors.position_player.run, componentEval.run_override).grade}
                          confidence={calcToolGrade(componentEval.run_tool, toolFactors.position_player.run, componentEval.run_override).confidence}
                          expanded={expandedTools.run}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, run: !prev.run }))}
                        />

                        {/* Field Tool */}
                        <ToolSection
                          title="Field Tool"
                          description="Glove (50%) + Range (25%) + First Step (25%)"
                          factors={toolFactors.position_player.field}
                          values={componentEval.field_tool}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, field_tool: v }))}
                          overrideGrade={componentEval.field_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, field_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.field_tool, toolFactors.position_player.field, componentEval.field_override).grade}
                          confidence={calcToolGrade(componentEval.field_tool, toolFactors.position_player.field, componentEval.field_override).confidence}
                          expanded={expandedTools.field}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, field: !prev.field }))}
                        />

                        {/* Arm Tool */}
                        <ToolSection
                          title="Arm Tool"
                          description="Arm Strength (50%) + Accuracy (25%) + Carry (25%)"
                          factors={toolFactors.position_player.arm}
                          values={componentEval.arm_tool}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, arm_tool: v }))}
                          overrideGrade={componentEval.arm_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, arm_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.arm_tool, toolFactors.position_player.arm, componentEval.arm_override).grade}
                          confidence={calcToolGrade(componentEval.arm_tool, toolFactors.position_player.arm, componentEval.arm_override).confidence}
                          expanded={expandedTools.arm}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, arm: !prev.arm }))}
                        />
                      </div>
                    )}

                    {/* Pitcher Tools */}
                    {isPitcher && toolFactors && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Pitcher Tools</h3>
                        
                        <ToolSection
                          title="Velocity"
                          description="Velo Peak (40%) + Velo Sustain (30%) + Life/Movement (30%)"
                          factors={toolFactors.pitcher.velocity}
                          values={componentEval.pitcher_velocity}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_velocity: v }))}
                          overrideGrade={componentEval.pitcher_velocity_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_velocity_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.pitcher_velocity, toolFactors.pitcher.velocity, componentEval.pitcher_velocity_override).grade}
                          confidence={calcToolGrade(componentEval.pitcher_velocity, toolFactors.pitcher.velocity, componentEval.pitcher_velocity_override).confidence}
                          expanded={expandedTools.pitcher_velocity}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, pitcher_velocity: !prev.pitcher_velocity }))}
                        />

                        <ToolSection
                          title="Command"
                          description="Strike% (35%) + Location Control (35%) + Miss Bats (30%)"
                          factors={toolFactors.pitcher.command}
                          values={componentEval.pitcher_command}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_command: v }))}
                          overrideGrade={componentEval.pitcher_command_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_command_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.pitcher_command, toolFactors.pitcher.command, componentEval.pitcher_command_override).grade}
                          confidence={calcToolGrade(componentEval.pitcher_command, toolFactors.pitcher.command, componentEval.pitcher_command_override).confidence}
                          expanded={expandedTools.pitcher_command}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, pitcher_command: !prev.pitcher_command }))}
                        />

                        <ToolSection
                          title="Stuff"
                          description="Breaking Ball (40%) + Changeup (35%) + Pitch Mix (25%)"
                          factors={toolFactors.pitcher.stuff}
                          values={componentEval.pitcher_stuff}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_stuff: v }))}
                          overrideGrade={componentEval.pitcher_stuff_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_stuff_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.pitcher_stuff, toolFactors.pitcher.stuff, componentEval.pitcher_stuff_override).grade}
                          confidence={calcToolGrade(componentEval.pitcher_stuff, toolFactors.pitcher.stuff, componentEval.pitcher_stuff_override).confidence}
                          expanded={expandedTools.pitcher_stuff}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, pitcher_stuff: !prev.pitcher_stuff }))}
                        />

                        <ToolSection
                          title="Pitchability"
                          description="Sequencing (35%) + Adjustments (35%) + Compete (30%)"
                          factors={toolFactors.pitcher.pitchability}
                          values={componentEval.pitcher_pitchability}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_pitchability: v }))}
                          overrideGrade={componentEval.pitcher_pitchability_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, pitcher_pitchability_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.pitcher_pitchability, toolFactors.pitcher.pitchability, componentEval.pitcher_pitchability_override).grade}
                          confidence={calcToolGrade(componentEval.pitcher_pitchability, toolFactors.pitcher.pitchability, componentEval.pitcher_pitchability_override).confidence}
                          expanded={expandedTools.pitcher_pitchability}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, pitcher_pitchability: !prev.pitcher_pitchability }))}
                        />
                      </div>
                    )}

                    {/* Catcher Tools */}
                    {isCatcher && toolFactors && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Catcher Tools</h3>
                        
                        <ToolSection
                          title="Receive/Frame"
                          description="Presentation (40%) + Quiet Hands (30%) + Strike Stealing (30%)"
                          factors={toolFactors.catcher.receive}
                          values={componentEval.catcher_receive}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, catcher_receive: v }))}
                          overrideGrade={componentEval.catcher_receive_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, catcher_receive_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.catcher_receive, toolFactors.catcher.receive, componentEval.catcher_receive_override).grade}
                          confidence={calcToolGrade(componentEval.catcher_receive, toolFactors.catcher.receive, componentEval.catcher_receive_override).confidence}
                          expanded={expandedTools.catcher_receive}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, catcher_receive: !prev.catcher_receive }))}
                        />

                        <ToolSection
                          title="Throw"
                          description="Pop Time (40%) + Arm Strength (35%) + Accuracy (25%)"
                          factors={toolFactors.catcher.throw}
                          values={componentEval.catcher_throw}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, catcher_throw: v }))}
                          overrideGrade={componentEval.catcher_throw_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, catcher_throw_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.catcher_throw, toolFactors.catcher.throw, componentEval.catcher_throw_override).grade}
                          confidence={calcToolGrade(componentEval.catcher_throw, toolFactors.catcher.throw, componentEval.catcher_throw_override).confidence}
                          expanded={expandedTools.catcher_throw}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, catcher_throw: !prev.catcher_throw }))}
                        />

                        <ToolSection
                          title="Block"
                          description="Lateral Movement (40%) + Smother (35%) + Recovery (25%)"
                          factors={toolFactors.catcher.block}
                          values={componentEval.catcher_block}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, catcher_block: v }))}
                          overrideGrade={componentEval.catcher_block_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, catcher_block_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.catcher_block, toolFactors.catcher.block, componentEval.catcher_block_override).grade}
                          confidence={calcToolGrade(componentEval.catcher_block, toolFactors.catcher.block, componentEval.catcher_block_override).confidence}
                          expanded={expandedTools.catcher_block}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, catcher_block: !prev.catcher_block }))}
                        />

                        <ToolSection
                          title="Game Management"
                          description="Pitch Calling (40%) + Staff Communication (30%) + Game IQ (30%)"
                          factors={toolFactors.catcher.game_mgmt}
                          values={componentEval.catcher_game_mgmt}
                          onChange={(v) => setComponentEval(prev => ({ ...prev, catcher_game_mgmt: v }))}
                          overrideGrade={componentEval.catcher_game_mgmt_override}
                          onOverrideChange={(v) => setComponentEval(prev => ({ ...prev, catcher_game_mgmt_override: v }))}
                          calculatedGrade={calcToolGrade(componentEval.catcher_game_mgmt, toolFactors.catcher.game_mgmt, componentEval.catcher_game_mgmt_override).grade}
                          confidence={calcToolGrade(componentEval.catcher_game_mgmt, toolFactors.catcher.game_mgmt, componentEval.catcher_game_mgmt_override).confidence}
                          expanded={expandedTools.catcher_game_mgmt}
                          onToggle={() => setExpandedTools(prev => ({ ...prev, catcher_game_mgmt: !prev.catcher_game_mgmt }))}
                        />
                      </div>
                    )}

                    {/* Raw Metrics Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Raw Metrics</h3>
                      <p className="text-sm text-gray-500 mb-4">Raw metrics can auto-populate sub-factors or be used alongside tool grades.</p>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-gray-600">60-Yard Dash</Label>
                          <Input
                            placeholder="e.g., 6.8"
                            value={componentEval.sixty_time}
                            onChange={(e) => setComponentEval(prev => ({ ...prev, sixty_time: e.target.value }))}
                            data-testid="sixty-time-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">Exit Velocity</Label>
                          <Input
                            placeholder="e.g., 91"
                            value={componentEval.exit_velocity}
                            onChange={(e) => setComponentEval(prev => ({ ...prev, exit_velocity: e.target.value }))}
                            data-testid="exit-velo-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">Bat Speed</Label>
                          <Input
                            placeholder="e.g., 68"
                            value={componentEval.bat_speed}
                            onChange={(e) => setComponentEval(prev => ({ ...prev, bat_speed: e.target.value }))}
                          />
                        </div>
                        {isPitcher && (
                          <div>
                            <Label className="text-xs text-gray-600">Fastball Velo</Label>
                            <Input
                              placeholder="e.g., 89"
                              value={componentEval.fastball_velocity}
                              onChange={(e) => setComponentEval(prev => ({ ...prev, fastball_velocity: e.target.value }))}
                            />
                          </div>
                        )}
                        {!isPitcher && (
                          <div>
                            <Label className="text-xs text-gray-600">Throwing Velo (IF)</Label>
                            <Input
                              placeholder="e.g., 82"
                              value={componentEval.infield_velocity}
                              onChange={(e) => setComponentEval(prev => ({ ...prev, infield_velocity: e.target.value }))}
                            />
                          </div>
                        )}
                        {isCatcher && (
                          <div>
                            <Label className="text-xs text-gray-600">Pop Time</Label>
                            <Input
                              placeholder="e.g., 2.0"
                              value={componentEval.pop_time}
                              onChange={(e) => setComponentEval(prev => ({ ...prev, pop_time: e.target.value }))}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Evaluation Notes Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Evaluation Notes</h3>
                      <div className="space-y-4">
                        {/* Voice Memo */}
                        <div>
                          <Label className="text-sm font-medium text-gray-700 mb-2 block">Voice Memo</Label>
                          <div className="flex items-center gap-3">
                            <Button
                              variant={recording ? "destructive" : "outline"}
                              onClick={recording ? stopRecording : startRecording}
                              data-testid="record-memo-btn"
                            >
                              {recording ? <MicOff className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
                              {recording ? "Stop Recording" : "Record Memo"}
                            </Button>
                            {componentEval.voice_transcript && (
                              <span className="text-sm text-green-600 flex items-center gap-1">
                                <CheckCircle className="h-4 w-4" />
                                Transcribed
                              </span>
                            )}
                          </div>
                          {componentEval.voice_transcript && (
                            <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                              <strong>Transcript:</strong> {componentEval.voice_transcript}
                            </div>
                          )}
                        </div>

                        {/* Written Notes */}
                        <div>
                          <Label className="text-sm font-medium text-gray-700 mb-2 block">Written Notes</Label>
                          <textarea
                            className="w-full min-h-[120px] p-3 border border-gray-200 rounded-lg text-sm"
                            placeholder="Detailed evaluation notes..."
                            value={componentEval.written_notes}
                            onChange={(e) => setComponentEval(prev => ({ ...prev, written_notes: e.target.value }))}
                            data-testid="written-notes-input"
                          />
                        </div>

                        {/* Evaluation Context */}
                        <div>
                          <Label className="text-sm font-medium text-gray-700 mb-2 block">Evaluation Context</Label>
                          <Input
                            placeholder="e.g., Dallas Regional Combine, Feb 2025"
                            value={componentEval.evaluation_context}
                            onChange={(e) => setComponentEval(prev => ({ ...prev, evaluation_context: e.target.value }))}
                            data-testid="eval-context-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <Button
                        size="lg"
                        onClick={handleSubmitEvaluation}
                        disabled={submitting}
                        className="bg-green-600 hover:bg-green-700 text-white px-8"
                        data-testid="save-evaluation-btn"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Evaluation
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <Target className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Player</h3>
                    <p className="text-gray-500">Choose a player from the list to begin the component-based evaluation</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            {selectedPlayer ? (
              <EvalHistoryInline 
                athleteId={selectedPlayer.athlete_id} 
                athleteName={selectedPlayer.name}
              />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Evaluation History</h3>
                <p className="text-gray-500">Select an athlete to view their evaluation history and scout comparisons.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
